import React, { createContext, useContext, useState } from 'react';

const OpenBusinessForm = createContext<() => void>(() => {});

export const useBusinessForm = () => useContext(OpenBusinessForm);

const empty = {
  businessName: '',
  organizationNumber: '',
  address: '',
  contactName: '',
  email: '',
  phone: '',
  message: '',
};

export const BusinessSignup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const close = () => {
    setOpen(false);
    setError('');
    setDone(false);
    setForm(empty);
  };

  const set = (key: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((current) => ({ ...current, [key]: e.target.value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const response = await fetch('/api/business-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(body.error || 'Kunne ikke sende søknaden');
        return;
      }
      setDone(true);
    } catch {
      setError('Kunne ikke sende søknaden. Prøv igjen.');
    } finally {
      setSending(false);
    }
  };

  return (
    <OpenBusinessForm.Provider value={() => setOpen(true)}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-gray-950/50 p-4" onClick={close}>
          <div
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 sm:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-sm font-semibold tracking-[0.16em] text-teal-700">FOR BEDRIFTER</p>
                <h2 className="text-2xl font-bold text-gray-900 mt-1">Kom i gang med bedriften din</h2>
              </div>
              <button type="button" onClick={close} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" aria-label="Lukk">
                ×
              </button>
            </div>
            {done ? (
              <div>
                <p className="text-gray-700 leading-relaxed">
                  Takk. Søknaden er sendt. Vi ser på den og tar kontakt når bedriften er godkjent.
                </p>
                <button type="button" onClick={close} className="mt-6 w-full rounded-full bg-teal-600 py-3 font-semibold text-white hover:bg-teal-700">
                  Lukk
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <Field label="Bedriftsnavn" value={form.businessName} onChange={set('businessName')} required />
                <Field label="Organisasjonsnummer" value={form.organizationNumber} onChange={set('organizationNumber')} required placeholder="123 456 789" />
                <Field label="Adresse" value={form.address} onChange={set('address')} required />
                <Field label="Kontaktperson" value={form.contactName} onChange={set('contactName')} required />
                <Field label="E-post" type="email" value={form.email} onChange={set('email')} required />
                <Field label="Telefon" value={form.phone} onChange={set('phone')} required placeholder="123 45 678" />
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Kort om bedriften</span>
                  <textarea
                    value={form.message}
                    onChange={set('message')}
                    rows={3}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:border-teal-500"
                    placeholder="Kafé, frisør, hva kundene skal stemple for"
                  />
                </label>
                {error && <p className="text-sm text-rose-600">{error}</p>}
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full rounded-full bg-teal-600 py-3 font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
                >
                  {sending ? 'Sender…' : 'Send søknad'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </OpenBusinessForm.Provider>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, required, type = 'text', placeholder }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:border-teal-500"
    />
  </label>
);
